// SOURCE-OF-TRUTH LOCK: Preview-only full APK journey. Must not replace /patient or /patient/home. Not production home.
import { useState, createContext, useContext } from 'react';
import { T } from '@design/index';
import NHCLogo from '@design/NHCLogo';

import EcosystemIntroScreen from './entry/EcosystemIntroScreen';
import TrustIntroScreen from './entry/TrustIntroScreen';
import RoleChoiceScreen from './entry/RoleChoiceScreen';
import OnboardingScreen from './entry/OnboardingScreen';
import EntryChoiceScreen from './entry/EntryChoiceScreen';
import LoginScreen from './entry/LoginScreen';
import PatRegStartScreen from './entry/PatRegStartScreen';
import PatRegBasicScreen from './entry/PatRegBasicScreen';
import PatRegContactScreen from './entry/PatRegContactScreen';
import PatRegOTPScreen from './entry/PatRegOTPScreen';
import PatRegHealthScreen from './entry/PatRegHealthScreen';
import PatRegEmergencyScreen from './entry/PatRegEmergencyScreen';
import PatRegAbhaScreen from './entry/PatRegAbhaScreen';
import PatRegConsentScreen from './entry/PatRegConsentScreen';
import PatRegNHCIDScreen from './entry/PatRegNHCIDScreen';
import PatRegCardScreen from './entry/PatRegCardScreen';
import PatRegSuccessScreen from './entry/PatRegSuccessScreen';
import ApprovalPendingScreen from './entry/ApprovalPendingScreen';
import PatientHome from './PatientHome';

export const ApkPreviewContext = createContext(false);
export function useIsApkPreview() { return useContext(ApkPreviewContext); }

function ApkSplashScreen() {
  return (
    <div style={{
      minHeight: '100%', background: `linear-gradient(170deg, ${T.navyDk} 0%, ${T.overlap} 55%, ${T.teal} 100%)`,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      gap: 14, position: 'relative', overflow: 'hidden', fontFamily: T.font,
    }}>
      <svg width="390" height="60" viewBox="0 0 390 60" fill="none" style={{ position: 'absolute', top: '50%', left: 0, transform: 'translateY(60px)', opacity: 0.12 }}>
        <path d="M0,30 L80,30 L100,30 L120,30 L140,30 L155,30 L165,48 L180,6 L195,48 L205,30 L220,30 L260,30 L300,30 L340,30 L390,30" stroke="#FFF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      </svg>
      <NHCLogo w={90} variant="white" />
      <div style={{ fontSize: 26, fontWeight: 800, color: '#FFF', letterSpacing: '-0.02em', marginTop: 6 }}>NeoHealthCard™</div>
      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.8)', letterSpacing: '0.02em', textAlign: 'center', padding: '0 40px', lineHeight: 1.6, marginTop: 4 }}>
        Transforming Healthcare with Security, Intelligence, and Care.
      </div>
      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)', letterSpacing: '0.02em', textAlign: 'center', padding: '0 36px', lineHeight: 1.7, marginTop: 2 }}>
        One connected health identity for patients, doctors, providers, emergency care, and verified records.
      </div>
      <div style={{ width: 160, height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.12)', marginTop: 30, overflow: 'hidden' }}>
        <div style={{ width: '60%', height: '100%', borderRadius: 2, background: 'linear-gradient(90deg, rgba(255,255,255,0.2), rgba(255,255,255,0.7))', animation: 'apkLoadPulse 1.5s ease-in-out infinite' }} />
      </div>
      <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.45)', textAlign: 'center', padding: '0 32px', lineHeight: 1.7, marginTop: 20 }}>
        Secure health identity · Consent-based records · Verified care network
      </div>
      <style>{`@keyframes apkLoadPulse { 0%{transform:translateX(-100%)} 100%{transform:translateX(180%)} }`}</style>
    </div>
  );
}

const STEPS: { key: string; label: string; component: React.ComponentType; noNav?: boolean }[] = [
  { key: 'splash', label: 'Splash', component: ApkSplashScreen, noNav: true },
  { key: 'trust', label: 'Trust Intro', component: TrustIntroScreen },
  { key: 'role', label: 'Role Choice', component: RoleChoiceScreen },
  { key: 'onboarding', label: 'Onboarding', component: OnboardingScreen },
  { key: 'login', label: 'Login Options', component: EntryChoiceScreen },
  { key: 'login-phone', label: 'Phone Login', component: LoginScreen },
  { key: 'reg-start', label: 'Register: Start', component: PatRegStartScreen },
  { key: 'reg-basic', label: 'Register: Basic', component: PatRegBasicScreen },
  { key: 'reg-contact', label: 'Register: Contact', component: PatRegContactScreen },
  { key: 'reg-otp', label: 'Register: OTP', component: PatRegOTPScreen },
  { key: 'reg-health', label: 'Register: Health', component: PatRegHealthScreen },
  { key: 'reg-emergency', label: 'Register: Emergency', component: PatRegEmergencyScreen },
  { key: 'reg-abha', label: 'Register: ABHA', component: PatRegAbhaScreen },
  { key: 'reg-consent', label: 'Register: Consent', component: PatRegConsentScreen },
  { key: 'reg-nhcid', label: 'Register: NeoHealthCard ID', component: PatRegNHCIDScreen },
  { key: 'reg-card', label: 'Register: Card', component: PatRegCardScreen },
  { key: 'reg-success', label: 'Register: Success', component: PatRegSuccessScreen },
  { key: 'approval', label: 'Approval Pending', component: ApprovalPendingScreen },
  { key: 'home', label: 'Patient Home', component: PatientHome },
];


export default function ApkFullFlowPreview() {
  const [step, setStep] = useState(0);
  const [showMenu, setShowMenu] = useState(false);
  const current = STEPS[step];
  const Screen = current.component;
  const isHome = current.key === 'home';

  return (
    <ApkPreviewContext.Provider value={true}>
    <div style={{ display: 'flex', flexDirection: 'column', height: '100dvh', background: '#F1F5F9', fontFamily: T.font }}>
      {/* Top bar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '6px 12px', background: T.navyDk, color: '#FFF', flexShrink: 0,
        fontSize: 11, fontWeight: 600, zIndex: 100,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 9, background: '#EA580C', padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>APK PREVIEW</span>
          <span>{step + 1}/{STEPS.length}</span>
          <span style={{ opacity: 0.7 }}>·</span>
          <span style={{ opacity: 0.8 }}>{current.label}</span>
        </div>
        <button onClick={() => setShowMenu(!showMenu)} style={{
          background: 'rgba(255,255,255,0.15)', border: 'none', color: '#FFF', fontSize: 10,
          padding: '3px 8px', borderRadius: 4, cursor: 'pointer', fontFamily: T.font, fontWeight: 600,
        }}>
          {showMenu ? 'Close' : 'Jump ▾'}
        </button>
      </div>

      {/* Jump menu */}
      {showMenu && (
        <div style={{
          position: 'absolute', top: 32, right: 8, zIndex: 200, background: T.white,
          border: `1px solid ${T.border}`, borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          maxHeight: 400, overflowY: 'auto', width: 220,
        }}>
          {STEPS.map((s, i) => (
            <button key={s.key} onClick={() => { setStep(i); setShowMenu(false); }} style={{
              display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px',
              background: i === step ? `${T.teal}15` : 'transparent',
              border: 'none', borderBottom: `1px solid ${T.border}`,
              fontSize: 11, fontWeight: i === step ? 700 : 500,
              color: i === step ? T.teal : T.text, cursor: 'pointer', fontFamily: T.font,
            }}>
              <span style={{ opacity: 0.5, marginRight: 6 }}>{i + 1}.</span>{s.label}
            </button>
          ))}
        </div>
      )}

      {/* Screen content — pointer-events:none prevents entry screens from navigating away */}
      <div key={current.key} style={{
        flex: 1, overflow: 'auto', position: 'relative',
        maxWidth: 390, width: '100%', margin: '0 auto',
        background: current.noNav ? 'transparent' : T.white,
        boxShadow: '0 0 20px rgba(0,0,0,0.08)',
        pointerEvents: isHome ? 'auto' : 'none',
      }}>
        <Screen />
      </div>

      {/* Bottom controls */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '8px 16px', background: T.navyDk, flexShrink: 0, maxWidth: 390,
        width: '100%', margin: '0 auto', boxSizing: 'border-box',
      }}>
        <button
          onClick={() => setStep(Math.max(0, step - 1))}
          disabled={step === 0}
          style={{
            background: step === 0 ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.2)',
            border: 'none', color: step === 0 ? 'rgba(255,255,255,0.3)' : '#FFF',
            fontSize: 11, fontWeight: 700, padding: '8px 18px', borderRadius: 8,
            cursor: step === 0 ? 'default' : 'pointer', fontFamily: T.font,
          }}
        >
          ← Back
        </button>
        <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.5)' }}>
          {isHome ? 'Home — click services above' : 'APK Flow Preview'}
        </span>
        <button
          onClick={() => setStep(Math.min(STEPS.length - 1, step + 1))}
          disabled={step === STEPS.length - 1}
          style={{
            background: step === STEPS.length - 1 ? 'rgba(255,255,255,0.08)' : '#EA580C',
            border: 'none', color: step === STEPS.length - 1 ? 'rgba(255,255,255,0.3)' : '#FFF',
            fontSize: 11, fontWeight: 700, padding: '8px 18px', borderRadius: 8,
            cursor: step === STEPS.length - 1 ? 'default' : 'pointer', fontFamily: T.font,
          }}
        >
          Next →
        </button>
      </div>
    </div>
    </ApkPreviewContext.Provider>
  );
}
